import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Settings,
  LogOut,
  PenSquare,
  Mail,
  Component,
  Menu,
  X,
  Wallet,
  ChevronDown,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import type { WorkspacesResult } from '@blogforge/shared';
import { useAuthStore } from '../stores/authStore.js';
import { api } from '../hooks/api.js';
import { purgeWorkspaceCaches } from '../lib/workspace-cache.js';
import { WorkspaceSwitcher } from './WorkspaceSwitcher.js';
import { SkillsSection } from './SkillsSection.js';

type NavItem = { to: string; label: string; icon: LucideIcon; end?: boolean };
type NavGroupChild = { to: string; label: string; end?: boolean };

const NAV_ITEMS: NavItem[] = [
  { to: '/posts', label: 'Posts', icon: FileText, end: true },
  { to: '/components', label: 'Components', icon: Component },
  { to: '/billing', label: 'Billing', icon: Wallet },
  { to: '/settings', label: 'Settings', icon: Settings },
];

/** Platform administration — only rendered for `isPlatformAdmin` users. */
const ADMIN_ITEM: NavItem = { to: '/admin', label: 'System admin', icon: ShieldCheck };

// Emails is a group — the row navigates to Content, with Send / Senders /
// Lists / History as sub-navigation.
const EMAIL_GROUP: NavGroupChild[] = [
  { to: '/emails', label: 'Content', end: true },
  { to: '/emails/send', label: 'Send' },
  { to: '/emails/senders', label: 'Senders' },
  { to: '/emails/lists', label: 'Lists' },
  { to: '/emails/history', label: 'History' },
  { to: '/emails/unsubscribes', label: 'Unsubscribes' },
];

export function AppShell() {
  const { user, logout, setUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [navOpen, setNavOpen] = useState(false);
  const [emailsOpen, setEmailsOpen] = useState(true);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setNavOpen(false), [location.pathname]);

  // Expand the Emails group while anywhere inside /emails; collapse it
  // again once the user navigates elsewhere.
  const inEmails = location.pathname === '/emails' || location.pathname.startsWith('/emails/');
  useEffect(() => setEmailsOpen(inEmails), [inEmails]);

  /**
   * Re-read the session once per mount. The store is hydrated from
   * localStorage, so anything the server knows and the token does not carry —
   * `isPlatformAdmin`, a changed content role, a deactivated account — would
   * otherwise stay stale until the next sign-in.
   */
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.auth.me(),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (me.data) setUser(me.data);
  }, [me.data, setUser]);

  /**
   * Send the owner of a never-set-up workspace into onboarding.
   *
   * Reads the same `['workspaces']` query the switcher uses (no extra
   * request), and only fires for roles that can actually finish the wizard —
   * a plain member must not be redirected to a page whose writes would 403.
   */
  const workspaces = useQuery<WorkspacesResult>({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
    staleTime: 30_000,
  });
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const pendingSetup = workspaces.data?.workspaces.find(
    (w) => w.id === activeWorkspaceId && w.needsSetup,
  );
  useEffect(() => {
    if (pendingSetup) navigate('/workspace/setup');
  }, [pendingSetup, navigate]);

  const handleLogout = async () => {
    await api.auth.logout().catch(() => null);
    // The query cache outlives the session, so without this the next person
    // to sign in on this browser would see the previous user's workspaces.
    await purgeWorkspaceCaches(qc);
    logout();
    navigate('/login');
  };

  const initials = (user?.name ?? '?').charAt(0).toUpperCase();

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden lg:grid lg:grid-cols-[var(--sidebar-width)_1fr] lg:grid-rows-1"
      style={{
        backgroundImage:
          'radial-gradient(42% 34% at 0% 0%, color-mix(in srgb, var(--color-accent) 7%, transparent), transparent 70%)',
      }}
    >
      {/* ── Mobile top bar ──────────────────────────────────── */}
      <header className="border-line bg-bg-soft/85 z-40 flex h-14 shrink-0 items-center gap-3 border-b px-4 backdrop-blur-xl lg:hidden">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation"
          className="btn-ghost h-9 w-9 rounded-lg px-0"
        >
          <Menu size={18} />
        </button>

        <button
          type="button"
          onClick={() => navigate('/posts')}
          className="flex min-w-0 items-center gap-2.5 text-left"
        >
          <span
            className="text-mark-ink flex h-7 w-7 shrink-0 items-center justify-center rounded-lg shadow-sm"
            style={{ background: 'var(--grad-brand-text)' }}
          >
            <PenSquare size={13} strokeWidth={2.2} />
          </span>
          <span className="text-ink truncate text-[15px] font-bold tracking-tight">BlogForger</span>
        </button>

        <button
          type="button"
          onClick={() => setNavOpen(true)}
          className="active:bg-ink/5 ml-auto flex items-center gap-2 rounded-full py-1 pr-3 pl-1 transition-colors"
          aria-label="Open navigation"
        >
          <span className="bg-primary text-primary-ink flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold">
            {initials}
          </span>
          <span className="text-ink-soft hidden max-w-24 truncate text-xs font-semibold sm:block">
            {user?.name ?? 'User'}
          </span>
        </button>
      </header>

      {/* ── Mobile drawer backdrop ──────────────────────────── */}
      {navOpen && (
        <div
          className="bg-ink/40 fixed inset-0 z-40 backdrop-blur-sm lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar (drawer on mobile, static rail on desktop) ── */}
      <aside
        className={`border-line bg-bg-soft/95 fixed inset-y-0 left-0 z-50 flex h-full w-[var(--sidebar-width)] flex-col border-r backdrop-blur-xl transition-transform duration-300 ease-out lg:static lg:z-auto lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 pt-6 pb-5">
          <div
            className="text-mark-ink flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-md ring-1 ring-black/10"
            style={{ background: 'var(--grad-brand-text)' }}
          >
            <PenSquare size={17} strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-ink truncate text-[15px] font-bold tracking-tight">BlogForger</p>
            <p className="text-muted text-[11px] font-medium">Visual blog editor</p>
          </div>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="btn-ghost h-8 w-8 rounded-lg px-0 lg:hidden"
          >
            <X size={15} />
          </button>
        </div>

        <div className="mx-5">
          <div className="hairline" />
        </div>

        {/* Active workspace (credit/billing context) */}
        <div className="pt-3">
          <WorkspaceSwitcher />
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {[...NAV_ITEMS, ...(user?.isPlatformAdmin ? [ADMIN_ITEM] : [])].map(
            ({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} {...(end ? { end: true } : {})}>
                {({ isActive }) => (
                  <span
                    className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-all duration-150 ${
                      isActive
                        ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
                        : 'text-ink-soft hover:bg-ink/5 hover:text-ink'
                    }`}
                  >
                    <Icon
                      size={16}
                      strokeWidth={2}
                      className={
                        isActive
                          ? 'text-accent'
                          : 'text-muted group-hover:text-ink-soft transition-colors'
                      }
                    />
                    {label}
                  </span>
                )}
              </NavLink>
            ),
          )}

          {/* Emails group */}
          <div>
            <button
              type="button"
              onClick={() => {
                setEmailsOpen((open) => !open);
                navigate('/emails');
              }}
              className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold transition-all duration-150 ${
                inEmails
                  ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
                  : 'text-ink-soft hover:bg-ink/5 hover:text-ink'
              }`}
            >
              <Mail
                size={16}
                strokeWidth={2}
                className={
                  inEmails
                    ? 'text-accent'
                    : 'text-muted group-hover:text-ink-soft transition-colors'
                }
              />
              <span className="flex-1">Emails</span>
              <ChevronDown
                size={14}
                strokeWidth={2}
                className={`text-muted transition-transform duration-200 ${
                  emailsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {emailsOpen && (
              <div className="border-line mt-1 ml-3 space-y-0.5 border-l pl-3.5">
                {EMAIL_GROUP.map(({ to, label, end }) => (
                  <NavLink key={to} to={to} {...(end ? { end: true } : {})}>
                    {({ isActive }) => (
                      <span
                        className={`block rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-all duration-150 ${
                          isActive
                            ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
                            : 'text-muted hover:bg-ink/5 hover:text-ink-soft'
                        }`}
                      >
                        {label}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Skills — always visible: the downloadable markdown that teaches
            an AI assistant how to write this system's content, and how to
            drive it over the API. Pinned above the user card so it survives a
            long, scrolling nav. */}
        <SkillsSection />

        {/* User */}
        <div className="mx-3 mb-4">
          <div className="border-line bg-card rounded-2xl border p-3 shadow-sm">
            <div className="flex items-center gap-2.5">
              <div className="bg-primary text-primary-ink flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold">
                {initials}
              </div>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="text-ink truncate text-[13px] font-semibold">
                  {user?.name ?? 'User'}
                </p>
                <p className="text-muted truncate text-[11px] capitalize">
                  {user?.role ?? 'member'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="text-muted hover:bg-danger/8 hover:text-danger mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-medium transition-colors"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────── */}
      <main className="relative min-w-0 flex-1 overflow-hidden lg:h-full">
        <Outlet />
      </main>
    </div>
  );
}
